<template>
  <header class="parts-item-card-header">
    <div class="parts-item-card-header__left">
      {{ `${workPartNumber} ${workPart.NombreCliente}` }}
    </div>

    <div class="parts-item-card-header__right">
      <div
        v-if="workPart.Nombre_ParteProduccionEstado"
        class="parts-item-card-header__status"
        :style="statusStyle"
      >
        {{ workPart.Nombre_ParteProduccionEstado }}
      </div>

      <div
        v-if="finishedPartDate"
        class="parts-item-card-header__date parts-item-card-header__date--finish"
      >
        {{ finishedPartDate }}
      </div>

      <div
        v-else-if="workPart.FechaInicio"
        class="parts-item-card-header__date parts-item-card-header__date--start"
      >
        {{ formatDate(new Date(workPart.FechaInicio)) }}
      </div>

      <div class="parts-item-card-header__priority">
        {{ workPart.Prioridad }}
      </div>
    </div>
  </header>
</template>

<script setup>
// @ts-check
import { computed } from 'vue';
import { HiDateTimeSupport } from '@/js/common/HIConversionSupport';
import { getContrastColor } from '@/js/utils/color';

/** @import { PropType } from 'vue' */
/** @import { WorkPartItem } from '@/js/stores/repairParts' */

const props = defineProps({
  workPart: {
    /** @type {PropType<WorkPartItem>} */
    type: Object,
    default: null,
  },
});

const workPartNumber = computed(() => `${props.workPart.Serie ?? ''}/${props.workPart.Numero ?? ''}`);
const finishedPartDate = computed(() => {
  const data = props.workPart;
  if (!data || !data.FechaFinalizacionOrden) {
    return '';
  }

  const startDate = data.FechaInicio && formatDate(new Date(data.FechaInicio));
  const finishDate = formatDate(new Date(data.FechaFinalizacionOrden));

  return finishDate && finishDate !== startDate
    ? `${startDate} — ${finishDate}`
    : startDate;
});

const DEFAULT_STATUS_BG_COLOR = 'var(--his-orange)';

const statusStyle = computed(() => {
  const backgroundColor = props.workPart.Color_ParteProduccionEstado
    ? `#${props.workPart.Color_ParteProduccionEstado}`
    : DEFAULT_STATUS_BG_COLOR;
  const textColor = getContrastColor(backgroundColor);

  return {
    color: textColor,
    backgroundColor,
  };
});

/**
 * Formats date to the DD/MM/YYYY format.
 * @param {Date} date
 */
function formatDate(date) {
  return HiDateTimeSupport.FormatDateTime(date, 'DD/MM/YYYY');
}
</script>

<style scoped lang="scss">
.parts-item-card-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: var(--his-tierra-white);

  &__left {
    color: #253858;
    font-weight: bold;
  }

  &__right {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
  }

  &__status {
    color: #fff;
    padding: 6px;
    border-radius: 4px;
    font-size: small;
    text-transform: uppercase;
  }

  &__date {
    color: #fff;
    padding: 6px;
    border-radius: 4px;
    font-size: small;

    &--finish {
      background-color: var(--his-red);
    }

    &--start {
      background-color: var(--his-green);
    }
  }

  &__priority {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background-color: var(--his-steel-blue);
    border-radius: 50%;
    width: 32px;
    height: 32px;

    // To align with the "More options" button
    margin-right: var(--f7-button-padding-horizontal);
  }
}
</style>
